import React from 'react';

import { Seeds as SeedsIcon } from '@/components/icons';
import { SeedType } from '@/sections/seeds/types';

const defaultEmptyDescription =
  'Add seed domains and CIDR ranges to discover related assets and identify potential security risks.';

/**
 * Unified configuration for all seed types
 *
 * This eliminates 50+ lines of "if (type === 'preseed')" conditionals
 * by defining all type-specific behavior declaratively.
 *
 * **Design Pattern**: Configuration-driven architecture
 * - All type-specific behavior centralized in one file
 * - Easy to add new seed types without code changes
 * - Self-documenting through configuration structure
 *
 * **Usage**:
 * ```typescript
 * const config = getSeedTypeConfig('preseed');
 * const allowedFilters = getAllowedFilters('domain');
 * const defaultSort = getDefaultSort('ip');
 * const canTag = isActionAvailable('preseed', 'tag'); // false
 * ```
 *
 * @see {@link SEED_TYPE_CONFIGS} for complete configuration registry
 */
export interface SeedTypeConfig {
  /** Seed type identifier */
  type: SeedType;

  /** Neo4j/backend class values for this type */
  classValues: string[];

  /** Default sort configuration */
  defaultSort: {
    orderBy: string;
    descending: boolean;
  };

  /** Allowed filter fields for this type */
  allowedFilters: string[];

  /**
   * Field mappings (for adapter pattern)
   *
   * Maps standard Seed interface fields to type-specific field names.
   * Used primarily for preseed which has different field names (type, value, title)
   * that need to be mapped to standard Seed fields (group, identifier, name).
   */
  fieldMappings?: {
    identifier?: string; // Field to use for identifier (default: 'identifier')
    name?: string; // Field to use for name (default: 'name')
    group?: string; // Field to use for group (default: 'group')
  };

  /** Available actions for this seed type */
  actions: {
    approve?: boolean;
    reject?: boolean;
    delete?: boolean;
    freeze?: boolean;
    comment?: boolean;
    tag?: boolean;
    scan?: boolean;
    share?: boolean;
  };

  /** Column configuration */
  columns: {
    defaultVisible: string[];
    defaultOrder: string[];
    sortableFields: string[];
  };

  /** Empty state configuration */
  emptyState: {
    icon: React.ReactNode;
    title: string;
    description: string;
    actionLabel?: string;
  };
}

/**
 * Configuration registry for all seed types
 *
 * **SINGLE SOURCE OF TRUTH** for type-specific behavior
 *
 * This configuration replaces scattered conditionals throughout the codebase:
 * - shouldShowFilterField() - now uses allowedFilters
 * - cleanupFiltersForTab() - now uses allowedFilters
 * - useSeedFilters() - now uses defaultSort
 * - Action visibility - now uses actions config
 *
 * **Adding a new seed type**:
 * 1. Add type to SeedType union in seedConstants.types.ts
 * 2. Add configuration entry here
 * 3. No other code changes needed - system is fully config-driven
 */
export const SEED_TYPE_CONFIGS: Record<SeedType, SeedTypeConfig> = {
  domain: {
    type: 'domain',
    classValues: ['domain', 'tld'],
    defaultSort: {
      orderBy: 'identifier',
      descending: true,
    },
    allowedFilters: [
      'status',
      'origin',
      'tags',
      'email',
      'registrar',
      'registrant',
    ],
    actions: {
      approve: true,
      reject: true,
      delete: true,
      freeze: true,
      comment: true,
      tag: true,
      scan: true,
      share: true,
    },
    columns: {
      defaultVisible: [
        'status',
        'identifier',
        'registrant',
        'tags',
        'origin',
        'created',
        'visited',
      ],
      defaultOrder: [
        'status',
        'identifier',
        'registrant',
        'email',
        'registrar',
        'tags',
        'origin',
        'source',
        'created',
        'visited',
      ],
      sortableFields: ['identifier', 'status', 'origin', 'created', 'visited'],
    },
    emptyState: {
      icon: React.createElement(SeedsIcon, {
        className: 'text-chariot-stroke',
      }),
      title: 'No Seeds to Show Yet',
      description: defaultEmptyDescription,
      actionLabel: 'Get Started',
    },
  },

  ip: {
    type: 'ip',
    classValues: ['ipv4', 'ipv6', 'cidr'],
    defaultSort: {
      orderBy: 'identifier',
      descending: true,
    },
    allowedFilters: [
      'status',
      'origin',
      'tags',
      'country',
      'registrar',
      'registrant',
    ],
    actions: {
      approve: true,
      reject: true,
      delete: true,
      freeze: true,
      comment: true,
      tag: true,
      scan: true,
      share: true,
    },
    columns: {
      defaultVisible: [
        'status',
        'identifier',
        'country',
        'asname',
        'tags',
        'origin',
        'created',
        'visited',
      ],
      defaultOrder: [
        'status',
        'identifier',
        'country',
        'asname',
        'asnumber',
        'tags',
        'origin',
        'source',
        'created',
        'visited',
      ],
      sortableFields: ['identifier', 'status', 'origin', 'created', 'visited'],
    },
    emptyState: {
      icon: React.createElement(SeedsIcon, {
        className: 'text-chariot-stroke',
      }),
      title: 'No Seeds to Show Yet',
      description: defaultEmptyDescription,
      actionLabel: 'Get Started',
    },
  },

  webapplication: {
    type: 'webapplication',
    classValues: ['webapplication'],
    defaultSort: {
      orderBy: 'identifier',
      descending: true,
    },
    allowedFilters: ['status', 'origin', 'tags'],
    actions: {
      approve: true,
      reject: true,
      delete: true,
      freeze: true,
      comment: true,
      tag: true,
      scan: true, // Web apps support crawl + web scan actions
      share: true,
    },
    columns: {
      defaultVisible: [
        'status',
        'identifier',
        'tags',
        'origin',
        'created',
        'visited',
      ],
      defaultOrder: [
        'status',
        'identifier',
        'tags',
        'origin',
        'source',
        'created',
        'visited',
      ],
      sortableFields: ['identifier', 'status', 'origin', 'created', 'visited'],
    },
    emptyState: {
      icon: React.createElement(SeedsIcon, {
        className: 'text-chariot-stroke',
      }),
      title: 'No Seeds to Show Yet',
      description: defaultEmptyDescription,
      actionLabel: 'Get Started',
    },
  },

  preseed: {
    type: 'preseed',
    classValues: ['preseed'], // Not used (preseed uses labels, not class filter)
    defaultSort: {
      orderBy: 'type', // Preseed uses 'type' field directly (not 'group' until Phase 3)
      descending: true,
    },
    allowedFilters: ['status'], // Preseed only supports status filter
    fieldMappings: {
      identifier: 'value', // preseed.value → identifier
      name: 'title', // preseed.title → name
      group: 'type', // preseed.type → group (for sorting)
    },
    actions: {
      approve: true,
      reject: true,
      delete: false, // Preseed doesn't support delete
      freeze: false, // Preseed doesn't support freeze
      comment: false, // Preseed doesn't support comments
      tag: false, // Preseed doesn't support tags
      scan: false, // Preseed doesn't support scan
      share: false, // Preseed doesn't support share
    },
    columns: {
      defaultVisible: ['status', 'group', 'name', 'identifier', 'count'],
      defaultOrder: ['status', 'group', 'name', 'identifier', 'count'],
      sortableFields: ['group', 'status'], // Maps to 'type' and 'status' via fieldMappings
    },
    emptyState: {
      icon: React.createElement(SeedsIcon, {
        className: 'text-chariot-stroke',
      }),
      title: 'No Seeds to Show Yet',
      description: defaultEmptyDescription,
    },
  },
};

/**
 * Get configuration for a seed type
 *
 * @param type - The seed type
 * @returns Configuration object for that type
 *
 * @example
 * ```typescript
 * const config = getSeedTypeConfig('preseed');
 * console.log(config.defaultSort); // { orderBy: 'group', descending: true }
 * console.log(config.allowedFilters); // ['status']
 * ```
 */
export const getSeedTypeConfig = (type: SeedType): SeedTypeConfig => {
  return SEED_TYPE_CONFIGS[type];
};

/**
 * Get allowed filter fields for a seed type
 *
 * **REPLACES** shouldShowFilterField() logic with configuration lookup
 *
 * This function eliminates 50+ lines of conditionals by using the
 * declarative configuration system. Instead of checking "if preseed"
 * or "if domain", we simply look up the allowed filters.
 *
 * @param type - The seed type
 * @returns Array of filter field names that are allowed for this type
 *
 * @example
 * ```typescript
 * getAllowedFilters('preseed'); // ['status']
 * getAllowedFilters('domain'); // ['status', 'source', 'tags', 'email', 'registrar', 'registrant']
 * getAllowedFilters('ip'); // ['status', 'source', 'tags', 'country', 'registrar', 'registrant']
 * ```
 */
export const getAllowedFilters = (type: SeedType): string[] => {
  return SEED_TYPE_CONFIGS[type].allowedFilters;
};

/**
 * Get default sort for a seed type
 *
 * **REPLACES** scattered default sort logic throughout the codebase
 *
 * Each seed type has its own natural default sort:
 * - domain/ip/webapplication: Sort by 'identifier' (the main field)
 * - preseed: Sort by 'group' (the preseed type category)
 *
 * @param type - The seed type
 * @returns Default sort configuration with orderBy and descending
 *
 * @example
 * ```typescript
 * getDefaultSort('domain'); // { orderBy: 'identifier', descending: true }
 * getDefaultSort('preseed'); // { orderBy: 'group', descending: true }
 * ```
 */
export const getDefaultSort = (type: SeedType) => {
  return SEED_TYPE_CONFIGS[type].defaultSort;
};

/**
 * Check if an action is available for a seed type
 *
 * **REPLACES** action visibility conditionals scattered in components
 *
 * Different seed types support different actions:
 * - domain/ip/webapplication: Support most actions
 * - preseed: Only supports approve/reject (no tags, comments, delete, freeze)
 * - webapplication: No scan action
 *
 * @param type - The seed type
 * @param action - The action to check availability for
 * @returns true if action is available, false otherwise
 *
 * @example
 * ```typescript
 * isActionAvailable('preseed', 'tag'); // false - preseed doesn't support tags
 * isActionAvailable('domain', 'tag'); // true - domain supports tags
 * isActionAvailable('webapplication', 'scan'); // false - webapp doesn't support scan
 * isActionAvailable('domain', 'scan'); // true - domain supports scan
 * ```
 */
export const isActionAvailable = (
  type: SeedType,
  action: keyof SeedTypeConfig['actions']
): boolean => {
  return SEED_TYPE_CONFIGS[type].actions[action] === true;
};

/**
 * Get column configuration for a seed type
 *
 * @param type - The seed type
 * @returns Column configuration including visible columns, order, and sortable fields
 *
 * @example
 * ```typescript
 * const config = getColumnConfig('preseed');
 * console.log(config.defaultVisible); // ['status', 'group', 'name', 'identifier', 'count']
 * console.log(config.sortableFields); // ['group', 'status']
 * ```
 */
export const getColumnConfig = (type: SeedType) => {
  return SEED_TYPE_CONFIGS[type].columns;
};

/**
 * Get empty state configuration for a seed type
 *
 * @param type - The seed type
 * @returns Empty state configuration with icon, title, description
 *
 * @example
 * ```typescript
 * const emptyState = getEmptyState('preseed');
 * console.log(emptyState.title); // 'No Attributes found'
 * ```
 */
export const getEmptyState = (type: SeedType) => {
  return SEED_TYPE_CONFIGS[type].emptyState;
};
